import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import {
  Discharge,
  Vessel,
  Voyage,
  VesselsService,
  ProviderMeasurement,
  BillOfLading
} from '../../vessels.service';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { DateTimeHelpers } from '../../../../shared/helpers/date-time.helpers';
import { KeycloakService } from 'src/app/authentication/keycloak.service';
import { forkJoin } from 'rxjs';
import { MessageLevel, PgMessagesService } from '../../../../shared/services/pg-messages.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { MaterialAutoCompleteComponent } from 'src/app/shared/components/material-autocomplete/material-auto-complete.component';
import { MaterialDateRangeComponent, DateRange } from 'src/app/shared/components/material-date-picker/material-date-range.component';
import { MaterialOrganisationSelectorComponent } from 'src/app/shared/components/material-autocomplete/material-organisationselector.component';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MaterialPgInputComponent } from 'src/app/shared/components/material-pg-input/material-pg-input.component';
import { MaterialPgButtonComponent } from 'src/app/shared/components/material-pg-button/material-pg-button.component';
import { MaterialPdfViewerComponent } from 'src/app/shared/components/material-pdf-viewer/material-pdf-viewer.component';
import { MaterialChunkFileUploadComponent } from 'src/app/shared/components/material-file-uploader/material-chunk-file-upload.component';
import { DocumentReference } from 'src/app/documents/documents.service';
import { VoyageServiceSpa } from 'src/app/generated/services/transportation/voyages/voyageSpa';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-discharge',
  templateUrl: './discharge.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    MaterialAutoCompleteComponent,
    MaterialDateRangeComponent,
    MaterialOrganisationSelectorComponent,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MaterialPgInputComponent,
    MaterialPgButtonComponent,
    MaterialPdfViewerComponent,
    MaterialChunkFileUploadComponent
  ]
})

export class DischargeComponent implements OnInit {

  @Output() changed: EventEmitter<Voyage> = new EventEmitter();
  @Output() cancelled: EventEmitter<any> = new EventEmitter();
  key = 'Discharge';
  _uploadedFileId!: string;
  _selectedVoyage!: Voyage;
  _selectedDischarge!: Discharge;
  expectedVersion?: number;
  vessels: Vessel[] = [];
  dischargeDate!: DateRange;
  surveyorFig!: boolean;
  selectedVoyageBLNumbers: string[] = [];
  daughterVessel!: Vessel;

  surveyors: Organisation[] = [];
  consignees: Organisation[] = [];
  isSts = false;
  edit!: boolean;
  daughterVesselVoyages: Voyage[] = [];
  daughterVesselVoyage?: Voyage;

  productNames = ['Propane', 'Butane', 'LPG MIX'];

  portOrganisations: Organisation[] = [];
  port!: Organisation;
  consignee!: Organisation;
  surveyor!: Organisation;
  billOfLadingNumbers: string[] = [];
  assignedBillOfLadings: BillOfLading[] = [];
  availableBillOfLadingsToAssign: BillOfLading[] = [];
  measurements: ProviderMeasurement[] = [];
  tempMeasurement!: ProviderMeasurement;
  tempBillOfLading!: BillOfLading;
  showBillOfLading!: boolean;
  showMeasurement!: boolean;
  doc?: DocumentReference;

  @Input() set fileId(value: string) {
    this._uploadedFileId = value;
  }

  constructor(private _organisationsService: OrganisationService,
              private _vesselService: VesselsService,
              private _keycloakService: KeycloakService,
              private _pgMessagesService: PgMessagesService,
              private _voyagesService: VoyageServiceSpa,
              private _router: Router,
              private _activatedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    this._selectedDischarge = Discharge.create();
    this.dischargeDate = {
      startDate: new Date(),
      endDate: new Date()
    };
    this.tempMeasurement = ProviderMeasurement.create();
    this._activatedRoute.paramMap.subscribe(async p => {
      this._activatedRoute.queryParams.subscribe(p => {
        this._uploadedFileId = p['fileId'];
      });
      const result = await this._voyagesService.getVoyageById({
        id: p.get('id') || ''
      });
      this._selectedVoyage = result.data || Voyage.create();
      this.expectedVersion = result.version;
      forkJoin([
        this._organisationsService.get(),
        this._vesselService.getVessels()
      ]).subscribe(data => {
        this.vessels = data[1];
        this.consignees = data[0].filter(
          o => o.properties['isConsignee'] === true
        );

        this.portOrganisations = data[0].filter(
          o =>
            o.properties['isAnchorage'] === true ||
            o.properties['isPort'] === true
        );

        this.surveyors = data[0].filter(
          o => o.properties['isSurvey'] === true
        );

        this.selectedVoyageBLNumbers =
          this._selectedVoyage.billOfLadings.map(b => b.no);

        if (
          this._selectedVoyage.discharges.find(
            discharge => discharge.fileId === this._uploadedFileId
          )
        ) {
          this.editDischarge();
        }
      });
    });
  }
  hasRole(role: string) {
    return this._keycloakService.hasRole(role);
  }

  editDischarge() {
    this._selectedDischarge = this._selectedVoyage.discharges.find(discharge => discharge.fileId === this._uploadedFileId) ?? Discharge.create();
    this.edit = true;
    this.port = this.portOrganisations.find(p => p.name === this._selectedDischarge.portOrganisationId) ?? Organisation.create();
    this.consignee = this.consignees.find(c => c.name === this._selectedDischarge.consigneeOrganisationId) ?? Organisation.create();
    this.dischargeDate = {
      startDate: new Date(DateTimeHelpers.getDateTimeFromTimestamp(this.port.timezone, this._selectedDischarge.date)),
      endDate: new Date(DateTimeHelpers.getDateTimeFromTimestamp(this.port.timezone, this._selectedDischarge.date))
    };
    this.billOfLadingNumbers = this._selectedDischarge.billOfLadingNumbers;
    this.assignedBillOfLadings = this._selectedVoyage.billOfLadings
      .filter(b => this._selectedDischarge.billOfLadingNumbers.includes(b.no));

    this.measurements = this._selectedDischarge.measurements;
    this.assignAvailableBillOfLadings();
    this.isSts = (this.port.properties['isAnchorage']) ? true : false;
    if (this._selectedDischarge.sts) {
      this.daughterVessel = this.vessels.find(v => v.imo === this._selectedDischarge.sts?.vesselImo) ?? Vessel.create();
      this.getDaughterVesselVoyages(this.daughterVessel);
    }
  }

  assignAvailableBillOfLadings() {
    const usedBillOfLadings = this._selectedVoyage.discharges.flatMap(d => d.billOfLadingNumbers).concat(
      this.assignedBillOfLadings.filter(b => b && b.no).map(b => b.no));
    this.availableBillOfLadingsToAssign = this._selectedVoyage.billOfLadings
      .filter(b => !usedBillOfLadings.includes(b.no));
  }

  selectDate(dateRange: DateRange) {
    this._selectedDischarge.date = dateRange.startDate.getTime();
  }

  organisationValueFormatter(data: Organisation) {
    return data.nameTranslations['en'];
  }

  compareDates(first: number, second: number) {
    const dateOne = new Date(first);
    const dateTwo = new Date(second);
    return (
      dateOne.getFullYear() === dateTwo.getFullYear() &&
      dateOne.getMonth() === dateTwo.getMonth() &&
      dateOne.getDate() === dateTwo.getDate());
  }

  saveDischarge() {
    this._selectedDischarge.portOrganisationId = this.port.name;
    this._selectedDischarge.billOfLadingNumbers = this.assignedBillOfLadings.map(b => b.no);
    this._selectedDischarge.measurements = this.measurements;
    const selectedTimezoneOffset = this.portOrganisations.find(o => o.name === this._selectedDischarge.portOrganisationId)?.timezone;
    if (this.isSts) {
      this._selectedDischarge.sts!.vesselImo = this.daughterVessel.imo;
    }
    if (!selectedTimezoneOffset) {
      console.error(`Timezone offset is missing for selected organisation Id: ${this._selectedDischarge.portOrganisationId}`);
    } else {
      if (!this.isSts && this.consignee) {
        this._selectedDischarge.consigneeOrganisationId = this.consignee.name;
      }
      if (this.isSts && this.daughterVesselVoyages.length > 0) {
        this._selectedDischarge.sts!.vesselName = this.daughterVesselVoyages
          .find(v => this._selectedDischarge.sts!.shipVoyageNo === v.shipVoyageNo)?.vesselName;
      }
      this._selectedDischarge.date = DateTimeHelpers.offsetTimestampWithZone(this.dischargeDate.startDate.getTime(), selectedTimezoneOffset);
      if (!this.edit) {
        const duplications = this._selectedVoyage.discharges
          .filter(s => s.portOrganisationId === this._selectedDischarge.portOrganisationId
            && s.consigneeOrganisationId === this._selectedDischarge.consigneeOrganisationId
            && this.compareDates(this._selectedDischarge.date, s.date));
        if (duplications.length >= 1) {
          this._pgMessagesService.publishMessage({
            message: 'Duplicate Discharge',
            level: MessageLevel.Error,
            topic: 'Voyage'
          });
        } else {
          this._selectedVoyage.discharges.push(this._selectedDischarge);
          this.updateVoyage(this._selectedVoyage);
        }
      } else {
        this.updateVoyage(this._selectedVoyage);
      }
    }
  }
  updateVoyage(voyage: Voyage) {

    this._voyagesService.updateVoyage({data: voyage, expectedVersion: this.expectedVersion || 0}).then(() => {
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Info,
        message: `Updated voyage ${voyage.shipVoyageNo} for ${voyage.vesselName}`,
        topic: 'Voyage'
      });
      this._router.navigate(['vessels/voyage', voyage.id], {
        queryParams: {
          t: new Date().getTime()
        }
      }).then();
    });
  }
  showAppraisalPreview(app: Discharge) {
    this._selectedDischarge = app;
  }

  addNewMeasurement(providerName: string) {
    const measurement = {
      providerId: '',
      provider: providerName,
      primary: undefined,
      product: '',
      weight: 0
    };
    if (providerName === 'Mother Vessel Figure') {
      measurement.providerId = this._selectedVoyage.vesselImo;
    } else if (providerName === 'Daughter Vessel Figure') {
      this.surveyorFig = false;
      measurement.providerId = this.daughterVessel.imo;
    }
    this.tempMeasurement = measurement;
    this.showMeasurement = true;
    this.showBillOfLading = false;
  }

  addToBillOfLading() {
    if (this.tempBillOfLading) {
      this.assignedBillOfLadings.push(this.tempBillOfLading);
      this.showBillOfLading = false;
    }
  }

  addToMeasurements() {
    if (this.tempMeasurement.provider === 'Surveyor' || this.tempMeasurement.provider === 'Surveyor Loading') {
      this.tempMeasurement.providerId = this.surveyor.name;
    }
    this.measurements.push(this.tempMeasurement);
    this.showMeasurement = false;
    this.surveyor = Organisation.create();
  }

  removeMeasurement(index: number) {
    this.measurements.splice(index, 1);
  }

  dischargeLocation(organisation: Organisation) {
    if (organisation) {
      this.isSts = organisation.properties?.['isAnchorage'] === true;
      if (this.isSts) {
        this._selectedDischarge.sts = {
          vesselImo: '',
          vesselName: '',
          shipVoyageNo: ''
        };
      } else {
        this._selectedDischarge.sts = undefined;
      }
    }
  }

  getDaughterVesselVoyages(vessel: Vessel) {
    if (vessel) {
      this._vesselService.getVoyagesByIMO(vessel.imo).subscribe(voyages => {
        this.daughterVesselVoyages = voyages;
        this.daughterVesselVoyage = voyages.find(v => v.shipVoyageNo === this._selectedDischarge.sts!.shipVoyageNo);
      });
    }
  }

  providerFormatter(measurement: ProviderMeasurement) {
    if ((measurement.provider === 'Surveyor' || measurement.provider === 'Surveyor Loading') && this.surveyors) {
      return this.surveyors.find(s => s.name === measurement.providerId)?.nameTranslations['en'];
    } else if (measurement.provider === 'Mother Vessel Figure') {
      return this._selectedVoyage.vesselName;
    } else if (measurement.provider === 'Daughter Vessel Figure' && this._selectedDischarge.sts!.vesselName) {
      return this._selectedDischarge.sts!.vesselName;
    } else if (measurement.provider === 'Daughter Vessel Figure') {
      return this.daughterVessel.name!.name;
    }
  }

  addBl() {
    this.assignAvailableBillOfLadings();
    this.showBillOfLading = true;
    this.showMeasurement = false;
  }

  selectedAssignedBLChanged(i: number, bl: BillOfLading) {
    this.assignedBillOfLadings[i] = bl;
  }

  removeAssignedBL(i: number) {
    this.assignedBillOfLadings.splice(i, 1);
  }

  selectedMeasurementProviderChanged(organisation: Organisation) {
    this.tempMeasurement.providerId = organisation.name;
  }

  vesselFormatter(vessel: Vessel) {
    if (vessel) {
      return vessel.name!.name;
    }
    return '';
  }
  uploadDocument(docReference: DocumentReference) {
    this._selectedDischarge.fileId = docReference.id;
  }
  deleteDischarge() {
    if (confirm('Are you sure to delete this Discharge')) {
      const i = this._selectedVoyage.discharges.findIndex(d => d.fileId === this._selectedDischarge.fileId);
      this._selectedVoyage.discharges.splice(i, 1);
      this.changed.emit(this._selectedVoyage);
    }
  }
  backToVoyage(){
    this._router.navigate(['/vessels/voyage/', this._selectedVoyage?.id,]);
  }
  voyageFormatter(voyage: Voyage) {
    if (voyage) {
      return voyage.shipVoyageNo;
    }
    return '';
  }
  billOfLadingFormatter(billOfLading: BillOfLading) {
    if (billOfLading) {
      return billOfLading.no;
    }
    return '';
  }
  productFormatter(product: string) {
    if (product) {
      return product;
    }
    return '';
  }
}
